import { useEffect, useState, type FormEvent } from 'react';
import { Seitenkopf } from '../components/Seitenkopf';
import { Badge, type BadgeTon } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { EmptyState } from '../components/ui/EmptyState';
import { Input } from '../components/ui/Input';
import { IconMangel, IconPlus } from '../components/icons';
import { DEMO, api, ApiFehler, getToken } from '../lib/api';
import { formatDatum } from '../lib/format';

const feldKlasse =
  'w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-fg outline-none focus:border-accent';
const API_BASIS = (import.meta.env.VITE_API_URL ?? '/api').replace(/\/$/, '');

type Status = 'neu' | 'in_bearbeitung' | 'erledigt';
type Kategorie = 'heizung' | 'wasser' | 'elektro' | 'schluessel' | 'gemeinschaft' | 'sonstiges';

interface Mangel {
  id: string;
  objektId: string;
  objektBezeichnung?: string;
  massnahmeId: string | null;
  kategorie: Kategorie;
  titel: string;
  beschreibung: string | null;
  melder: string | null;
  status: Status;
  hatFoto: boolean;
  erstelltAm: string | null;
}

const STATUS_LABEL: Record<Status, string> = {
  neu: 'Neu',
  in_bearbeitung: 'In Bearbeitung',
  erledigt: 'Erledigt',
};
const STATUS_TON: Record<Status, BadgeTon> = {
  neu: 'warnung',
  in_bearbeitung: 'info',
  erledigt: 'erfolg',
};
const KATEGORIE_LABEL: Record<Kategorie, string> = {
  heizung: 'Heizung',
  wasser: 'Wasser',
  elektro: 'Elektro',
  schluessel: 'Schlüssel',
  gemeinschaft: 'Gemeinschaft',
  sonstiges: 'Sonstiges',
};

const DEMO_MAENGEL: Mangel[] = [
  {
    id: 'mg-1',
    objektId: 'obj-1',
    objektBezeichnung: 'Lindenstraße 14',
    massnahmeId: null,
    kategorie: 'heizung',
    titel: 'Heizung im Schlafzimmer bleibt kalt',
    beschreibung: 'Thermostat auf 5, wird trotzdem nicht warm.',
    melder: 'Wohnung 2. OG rechts',
    status: 'neu',
    hatFoto: true,
    erstelltAm: '2026-09-12',
  },
  {
    id: 'mg-2',
    objektId: 'obj-1',
    objektBezeichnung: 'Lindenstraße 14',
    massnahmeId: 'ma-x',
    kategorie: 'wasser',
    titel: 'Tropfender Wasserhahn Küche',
    beschreibung: null,
    melder: 'Wohnung EG links',
    status: 'in_bearbeitung',
    hatFoto: false,
    erstelltAm: '2026-09-05',
  },
];

export function Maengel() {
  const [maengel, setMaengel] = useState<Mangel[]>(DEMO ? DEMO_MAENGEL : []);
  const [laden, setLaden] = useState(!DEMO);
  const [fehler, setFehler] = useState<string | null>(null);
  const [neuOffen, setNeuOffen] = useState(false);
  const [portalOffen, setPortalOffen] = useState(false);

  useEffect(() => {
    if (DEMO) return;
    setLaden(true);
    api
      .get<{ maengel: Mangel[] }>('/maengel')
      .then((d) => {
        setMaengel(d.maengel);
        setFehler(null);
      })
      .catch((e) => setFehler(e instanceof ApiFehler ? e.message : 'Die Mängel konnten nicht geladen werden.'))
      .finally(() => setLaden(false));
  }, []);

  async function status(id: string, s: Status) {
    setMaengel((xs) => xs.map((m) => (m.id === id ? { ...m, status: s } : m)));
    if (DEMO) return;
    try {
      await api.patch(`/maengel/${id}`, { status: s });
    } catch (e) {
      setFehler(e instanceof ApiFehler ? e.message : 'Die Änderung konnte nicht gespeichert werden.');
    }
  }

  async function inMassnahme(id: string) {
    if (DEMO) {
      setMaengel((xs) => xs.map((m) => (m.id === id ? { ...m, massnahmeId: `ma-${Date.now()}`, status: 'in_bearbeitung' } : m)));
      return;
    }
    try {
      const r = await api.post<{ massnahmeId: string }>(`/maengel/${id}/massnahme`);
      setMaengel((xs) => xs.map((m) => (m.id === id ? { ...m, massnahmeId: r.massnahmeId, status: 'in_bearbeitung' } : m)));
    } catch (e) {
      setFehler(e instanceof ApiFehler ? e.message : 'Die Maßnahme konnte nicht erstellt werden.');
    }
  }

  const offen = maengel.filter((m) => m.status !== 'erledigt');
  const erledigt = maengel.filter((m) => m.status === 'erledigt');

  return (
    <div>
      <Seitenkopf
        titel="Mängelmelder"
        beschreibung="Gemeldete Mängel aus den Einheiten mit Foto und Kategorie. Aus einem Mangel wird per Klick eine Maßnahme (Arbeitsauftrag) für Ihren Dienstleister."
      />

      <div className="mb-4 flex flex-wrap justify-end gap-2">
        <Button variante="sekundaer" groesse="sm" onClick={() => setPortalOffen((o) => !o)}>
          Meldelinks für Mieter
        </Button>
        <Button icon={<IconPlus groesse={16} />} groesse="sm" onClick={() => setNeuOffen((o) => !o)}>
          Mangel erfassen
        </Button>
      </div>

      {portalOffen && <PortalPanel />}

      {neuOffen && (
        <NeuFormular
          onAngelegt={(m) => {
            setMaengel((xs) => [m, ...xs]);
            setNeuOffen(false);
          }}
          onAbbruch={() => setNeuOffen(false)}
        />
      )}

      {fehler && (
        <Card className="mb-4">
          <p className="text-sm text-danger">{fehler}</p>
        </Card>
      )}

      {laden ? (
        <p className="text-sm text-fg-muted">Wird geladen …</p>
      ) : (
        <div className="space-y-6">
          <section>
            <h2 className="mb-2 px-1 text-sm font-semibold uppercase tracking-wide text-fg-subtle">
              Offen ({offen.length})
            </h2>
            {offen.length === 0 ? (
              <EmptyState
                icon={<IconMangel groesse={22} />}
                titel="Keine offenen Mängel"
                beschreibung="Erfassen Sie einen Mangel oder lassen Sie Mieter über den Portallink melden."
              />
            ) : (
              <div className="space-y-2.5">
                {offen.map((m) => (
                  <MangelKarte key={m.id} m={m} onStatus={status} onMassnahme={inMassnahme} />
                ))}
              </div>
            )}
          </section>

          {erledigt.length > 0 && (
            <section>
              <h2 className="mb-2 px-1 text-sm font-semibold uppercase tracking-wide text-fg-subtle">
                Erledigt ({erledigt.length})
              </h2>
              <div className="space-y-2.5">
                {erledigt.map((m) => (
                  <MangelKarte key={m.id} m={m} onStatus={status} onMassnahme={inMassnahme} />
                ))}
              </div>
            </section>
          )}
        </div>
      )}
    </div>
  );
}

function MangelKarte({
  m,
  onStatus,
  onMassnahme,
}: {
  m: Mangel;
  onStatus: (id: string, s: Status) => void;
  onMassnahme: (id: string) => void;
}) {
  const [fotoUrl, setFotoUrl] = useState<string | null>(null);
  const [fotoOffen, setFotoOffen] = useState(false);

  async function fotoLaden() {
    setFotoOffen((o) => !o);
    if (fotoUrl || DEMO) return;
    try {
      const res = await fetch(`${API_BASIS}/maengel/${m.id}/foto`, {
        headers: getToken() ? { Authorization: `Bearer ${getToken()}` } : {},
      });
      if (!res.ok) return;
      const blob = await res.blob();
      setFotoUrl(URL.createObjectURL(blob));
    } catch {
      /* Foto nicht ladbar — Karte bleibt nutzbar. */
    }
  }

  return (
    <Card>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-semibold text-fg">{m.titel}</span>
            <Badge ton={STATUS_TON[m.status]}>{STATUS_LABEL[m.status]}</Badge>
            <Badge ton="neutral">{KATEGORIE_LABEL[m.kategorie]}</Badge>
            {m.massnahmeId && <Badge ton="info">Maßnahme angelegt</Badge>}
          </div>
          {m.beschreibung && <p className="mt-1 text-sm text-fg-muted">{m.beschreibung}</p>}
          <p className="mt-1 text-xs text-fg-subtle">
            {m.objektBezeichnung}
            {m.melder ? ` · ${m.melder}` : ''}
            {m.erstelltAm ? ` · gemeldet ${formatDatum(m.erstelltAm)}` : ''}
          </p>
        </div>
      </div>

      {m.hatFoto && (
        <div className="mt-2">
          <button className="text-xs font-medium text-accent hover:underline" onClick={fotoLaden}>
            {fotoOffen ? 'Foto ausblenden' : 'Foto ansehen'}
          </button>
          {fotoOffen && (
            <div className="mt-2">
              {DEMO || !fotoUrl ? (
                <div className="flex h-40 w-full max-w-xs items-center justify-center rounded-lg border border-border bg-surface-2 text-xs text-fg-subtle">
                  {DEMO ? 'Foto-Vorschau (im Live-Betrieb)' : 'Wird geladen …'}
                </div>
              ) : (
                <img src={fotoUrl} alt="Mangel" className="max-h-64 rounded-lg border border-border" />
              )}
            </div>
          )}
        </div>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-border pt-3">
        <select
          className={`${feldKlasse} max-w-[11rem]`}
          value={m.status}
          onChange={(e) => onStatus(m.id, e.target.value as Status)}
        >
          <option value="neu">Neu</option>
          <option value="in_bearbeitung">In Bearbeitung</option>
          <option value="erledigt">Erledigt</option>
        </select>
        {!m.massnahmeId && (
          <Button variante="sekundaer" groesse="sm" onClick={() => onMassnahme(m.id)}>
            In Maßnahme überführen
          </Button>
        )}
      </div>
    </Card>
  );
}

function dateiZuBase64(datei: File): Promise<{ base64: string; name: string; mime: string }> {
  return new Promise((resolve, reject) => {
    const leser = new FileReader();
    leser.onload = () => resolve({ base64: String(leser.result), name: datei.name, mime: datei.type });
    leser.onerror = () => reject(new Error('Datei konnte nicht gelesen werden.'));
    leser.readAsDataURL(datei);
  });
}

function NeuFormular({
  onAngelegt,
  onAbbruch,
}: {
  onAngelegt: (m: Mangel) => void;
  onAbbruch: () => void;
}) {
  const [objekte, setObjekte] = useState<{ id: string; bezeichnung: string }[]>([]);
  const [objektId, setObjektId] = useState('');
  const [kategorie, setKategorie] = useState<Kategorie>('sonstiges');
  const [titel, setTitel] = useState('');
  const [beschreibung, setBeschreibung] = useState('');
  const [melder, setMelder] = useState('');
  const [foto, setFoto] = useState<File | null>(null);
  const [sendet, setSendet] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  useEffect(() => {
    if (DEMO) {
      setObjekte([
        { id: 'obj-1', bezeichnung: 'Lindenstraße 14' },
        { id: 'obj-2', bezeichnung: 'Am Wasserturm 3' },
      ]);
      setObjektId('obj-1');
      return;
    }
    api
      .get<{ objekte: { id: string; bezeichnung: string }[] }>('/objekte')
      .then((d) => {
        setObjekte(d.objekte);
        setObjektId((g) => g || (d.objekte[0]?.id ?? ''));
      })
      .catch(() => setFehler('Objekte konnten nicht geladen werden.'));
  }, []);

  async function speichern(e: FormEvent) {
    e.preventDefault();
    setFehler(null);
    if (!objektId || !titel.trim()) {
      setFehler('Bitte Objekt und Kurzbeschreibung angeben.');
      return;
    }
    setSendet(true);
    try {
      let fotoDaten: { base64: string; name: string; mime: string } | null = null;
      if (foto) fotoDaten = await dateiZuBase64(foto);
      if (!DEMO) {
        const r = await api.post<{ mangel: Mangel }>('/maengel', {
          objektId,
          kategorie,
          titel: titel.trim(),
          beschreibung: beschreibung.trim() || undefined,
          melder: melder.trim() || undefined,
          fotoBase64: fotoDaten?.base64,
          fotoName: fotoDaten?.name,
          fotoMime: fotoDaten?.mime,
        });
        onAngelegt(r.mangel);
      } else {
        onAngelegt({
          id: `neu-${Date.now()}`,
          objektId,
          objektBezeichnung: objekte.find((o) => o.id === objektId)?.bezeichnung,
          massnahmeId: null,
          kategorie,
          titel: titel.trim(),
          beschreibung: beschreibung.trim() || null,
          melder: melder.trim() || null,
          status: 'neu',
          hatFoto: !!fotoDaten,
          erstelltAm: new Date().toISOString().slice(0, 10),
        });
      }
    } catch (err) {
      setFehler(err instanceof ApiFehler ? err.message : 'Der Mangel konnte nicht gespeichert werden.');
    } finally {
      setSendet(false);
    }
  }

  return (
    <Card titel="Mangel erfassen" className="mb-4">
      <form onSubmit={speichern} className="space-y-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <label className="block">
            <span className="mb-1.5 block text-sm font-medium text-fg">Objekt</span>
            <select className={feldKlasse} value={objektId} onChange={(e) => setObjektId(e.target.value)}>
              {objekte.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.bezeichnung}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="mb-1.5 block text-sm font-medium text-fg">Kategorie</span>
            <select className={feldKlasse} value={kategorie} onChange={(e) => setKategorie(e.target.value as Kategorie)}>
              {(Object.keys(KATEGORIE_LABEL) as Kategorie[]).map((k) => (
                <option key={k} value={k}>
                  {KATEGORIE_LABEL[k]}
                </option>
              ))}
            </select>
          </label>
        </div>
        <Input label="Kurzbeschreibung" placeholder="z. B. Heizung wird nicht warm" value={titel} onChange={(e) => setTitel(e.target.value)} />
        <label className="block">
          <span className="mb-1.5 block text-sm font-medium text-fg">Details (optional)</span>
          <textarea
            className={`${feldKlasse} min-h-[80px]`}
            value={beschreibung}
            onChange={(e) => setBeschreibung(e.target.value)}
          />
        </label>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input label="Melder (optional)" placeholder="z. B. Wohnung 2. OG" value={melder} onChange={(e) => setMelder(e.target.value)} />
          <label className="block">
            <span className="mb-1.5 block text-sm font-medium text-fg">Foto (optional)</span>
            <input
              type="file"
              accept="image/*"
              className="block w-full text-sm text-fg-muted file:mr-3 file:rounded-lg file:border-0 file:bg-accent-soft file:px-3 file:py-2 file:text-sm file:text-accent-soft-fg"
              onChange={(e) => setFoto(e.target.files?.[0] ?? null)}
            />
          </label>
        </div>
        {fehler && <p className="text-sm font-medium text-danger">{fehler}</p>}
        <div className="flex gap-2">
          <Button type="submit" disabled={sendet}>
            {sendet ? 'Wird gespeichert …' : 'Mangel melden'}
          </Button>
          <Button type="button" variante="dezent" onClick={onAbbruch}>
            Abbrechen
          </Button>
        </div>
      </form>
    </Card>
  );
}

interface PortalLink {
  id: string;
  objektId: string;
  objektBezeichnung?: string;
  einheitBezeichnung?: string | null;
  token: string;
  bezeichnung: string | null;
  aktiv: boolean;
  meldungen: number;
}

const DEMO_LINKS: PortalLink[] = [
  {
    id: 'pl-1',
    objektId: 'obj-1',
    objektBezeichnung: 'Lindenstraße 14',
    einheitBezeichnung: null,
    token: 'demo-token-lindenstrasse-14',
    bezeichnung: 'Aushang Treppenhaus',
    aktiv: true,
    meldungen: 3,
  },
];

/** Öffentliche URL, die der Mieter aufruft. Basisadresse = aktuelle Origin. */
function portalUrl(token: string): string {
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  return `${origin}/melden/${token}`;
}

function PortalPanel() {
  const [links, setLinks] = useState<PortalLink[]>(DEMO ? DEMO_LINKS : []);
  const [objekte, setObjekte] = useState<{ id: string; bezeichnung: string }[]>([]);
  const [objektId, setObjektId] = useState('');
  const [bezeichnung, setBezeichnung] = useState('');
  const [laden, setLaden] = useState(!DEMO);
  const [sendet, setSendet] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [kopiert, setKopiert] = useState<string | null>(null);

  useEffect(() => {
    if (DEMO) {
      setObjekte([
        { id: 'obj-1', bezeichnung: 'Lindenstraße 14' },
        { id: 'obj-2', bezeichnung: 'Am Wasserturm 3' },
      ]);
      setObjektId('obj-1');
      return;
    }
    Promise.all([
      api.get<{ links: PortalLink[] }>('/portal-links'),
      api.get<{ objekte: { id: string; bezeichnung: string }[] }>('/objekte'),
    ])
      .then(([l, o]) => {
        setLinks(l.links);
        setObjekte(o.objekte);
        setObjektId((g) => g || (o.objekte[0]?.id ?? ''));
        setFehler(null);
      })
      .catch((e) => setFehler(e instanceof ApiFehler ? e.message : 'Die Meldelinks konnten nicht geladen werden.'))
      .finally(() => setLaden(false));
  }, []);

  async function erstellen() {
    if (!objektId) {
      setFehler('Bitte ein Objekt wählen.');
      return;
    }
    setSendet(true);
    setFehler(null);
    try {
      if (DEMO) {
        setLinks((xs) => [
          {
            id: `pl-${Date.now()}`,
            objektId,
            objektBezeichnung: objekte.find((o) => o.id === objektId)?.bezeichnung,
            einheitBezeichnung: null,
            token: `demo-${Math.random().toString(36).slice(2, 10)}`,
            bezeichnung: bezeichnung.trim() || null,
            aktiv: true,
            meldungen: 0,
          },
          ...xs,
        ]);
      } else {
        const r = await api.post<{ link: PortalLink }>('/portal-links', {
          objektId,
          bezeichnung: bezeichnung.trim() || undefined,
        });
        setLinks((xs) => [r.link, ...xs]);
      }
      setBezeichnung('');
    } catch (e) {
      setFehler(e instanceof ApiFehler ? e.message : 'Der Meldelink konnte nicht erstellt werden.');
    } finally {
      setSendet(false);
    }
  }

  async function umschalten(l: PortalLink) {
    const neu = !l.aktiv;
    setLinks((xs) => xs.map((x) => (x.id === l.id ? { ...x, aktiv: neu } : x)));
    if (DEMO) return;
    try {
      await api.patch(`/portal-links/${l.id}`, { aktiv: neu });
    } catch (e) {
      setLinks((xs) => xs.map((x) => (x.id === l.id ? { ...x, aktiv: l.aktiv } : x)));
      setFehler(e instanceof ApiFehler ? e.message : 'Die Änderung konnte nicht gespeichert werden.');
    }
  }

  async function kopieren(token: string) {
    try {
      await navigator.clipboard.writeText(portalUrl(token));
      setKopiert(token);
      setTimeout(() => setKopiert((k) => (k === token ? null : k)), 2000);
    } catch {
      setFehler('Kopieren nicht möglich. Bitte den Link manuell markieren.');
    }
  }

  return (
    <Card titel="Meldelinks für Mieter" className="mb-4">
      <p className="mb-4 text-sm text-fg-muted">
        Ein anmeldefreier Link je Objekt. Als Aushang im Treppenhaus oder per Nachricht an die Mieter.
        Wer ihn öffnet, meldet einen Mangel ohne Konto — die Meldung landet direkt hier im Mängelmelder.
        Ein Link lässt sich jederzeit deaktivieren.
      </p>

      <div className="mb-4 flex flex-wrap items-end gap-2 border-b border-border pb-4">
        <label className="block min-w-[12rem] flex-1">
          <span className="mb-1.5 block text-sm font-medium text-fg">Objekt</span>
          <select className={feldKlasse} value={objektId} onChange={(e) => setObjektId(e.target.value)}>
            {objekte.map((o) => (
              <option key={o.id} value={o.id}>
                {o.bezeichnung}
              </option>
            ))}
          </select>
        </label>
        <label className="block min-w-[12rem] flex-1">
          <span className="mb-1.5 block text-sm font-medium text-fg">Bezeichnung (optional)</span>
          <input
            className={feldKlasse}
            placeholder="z. B. Aushang Treppenhaus"
            value={bezeichnung}
            onChange={(e) => setBezeichnung(e.target.value)}
          />
        </label>
        <Button groesse="sm" onClick={erstellen} disabled={sendet}>
          {sendet ? 'Wird erstellt …' : 'Link erstellen'}
        </Button>
      </div>

      {fehler && <p className="mb-3 text-sm font-medium text-danger">{fehler}</p>}

      {laden ? (
        <p className="text-sm text-fg-muted">Wird geladen …</p>
      ) : links.length === 0 ? (
        <p className="text-sm text-fg-muted">Noch kein Meldelink erstellt.</p>
      ) : (
        <div className="space-y-2.5">
          {links.map((l) => (
            <div key={l.id} className="rounded-lg border border-border bg-surface-2 px-3 py-2.5">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-semibold text-fg">{l.objektBezeichnung ?? 'Objekt'}</span>
                    {l.bezeichnung && <span className="text-xs text-fg-muted">{l.bezeichnung}</span>}
                    <Badge ton={l.aktiv ? 'erfolg' : 'neutral'}>{l.aktiv ? 'Aktiv' : 'Deaktiviert'}</Badge>
                    {l.meldungen > 0 && <Badge ton="info">{l.meldungen} Meldungen</Badge>}
                  </div>
                  <p className="mt-1 truncate font-mono text-xs text-fg-subtle">{portalUrl(l.token)}</p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <button
                    className="text-xs font-medium text-accent hover:underline"
                    onClick={() => kopieren(l.token)}
                  >
                    {kopiert === l.token ? 'Kopiert ✓' : 'Link kopieren'}
                  </button>
                  <button
                    className="text-xs font-medium text-fg-muted hover:underline"
                    onClick={() => umschalten(l)}
                  >
                    {l.aktiv ? 'Deaktivieren' : 'Aktivieren'}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

export default Maengel;
